import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { NOTIFIER } from '@shared/notifier/types';
import {
  Arrow,
  Point,
  Rect,
  Size,
  arrowHeadGeometry,
  markStrokeWidth,
  toSourceArrow,
  toSourceRect,
} from './support-image-editor.geometry';
import { SNIP_HANDLES, SnipHandle, handleCursor, moveRect, resizeRect } from './support-snip-geometry';

type SupportEditorMode = 'crop' | 'mark' | 'arrow';

/** Eén markering op het screenshot: een rood kader of een rode pijl (bronpixels). */
export type SupportAnnotation =
  | { readonly kind: 'rect'; readonly rect: Rect }
  | { readonly kind: 'arrow'; readonly arrow: Arrow };

/** Lopende sleep aan het crop-kader: een handle of het hele kader; `scale` = bronpixels per CSS-pixel. */
type CropDrag =
  | { kind: 'resize'; handle: SnipHandle; origin: Point; rect: Rect; scale: number; element: HTMLElement }
  | { kind: 'move'; origin: Point; rect: Rect; scale: number; element: HTMLElement };

const MARK_COLOR = '#e53935';
const SELECTION_FILL = 'rgba(212, 168, 105, 0.2)';
const SELECTION_STROKE = 'rgba(212, 168, 105, 0.9)';
/** Pijl in wording: zelfde rood, iets doorschijnend tot de sleep af is. */
const ARROW_DRAFT_ALPHA = 0.6;
const JPEG_QUALITY = 0.85;
/** Jpeg kent geen alpha: transparante delen worden anders zwart. */
const JPEG_BACKGROUND = '#ffffff';
/** Ruimte (CSS-pixels) die de afmetingenbox onder het kader nodig heeft; anders komt ze binnenin. */
const SIZE_LABEL_SPACE_PX = 26;

/**
 * Eenvoudige beeldeditor voor een screenshot. Het crop-kader ligt als HTML-laag
 * over het beeld en is, zoals in de snip-fase, met handles bij te stellen of te
 * verschuiven; het bepaalt de exportregio zonder de bron te wijzigen. Daarnaast
 * zoveel rode markeerkaders en pijlen als nodig, getekend op een overlay-canvas;
 * "ongedaan maken" (of Ctrl+Z) haalt de laatste weg. Alle state staat in
 * bronpixels; alleen de weergave rekent om naar CSS-pixels. De wiskunde zit in
 * support-image-editor.geometry.ts en support-snip-geometry.ts.
 */
@Component({
  selector: 'support-image-editor',
  imports: [SharedModule, TranslocoModule],
  templateUrl: './support-image-editor.component.html',
  styleUrl: './support-image-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(keydown.escape)': 'onEscape($event)',
    '(keydown.control.z)': 'onUndoKey($event)',
    '(keydown.meta.z)': 'onUndoKey($event)',
  },
})
export class SupportImageEditorComponent {
  readonly source = input.required<Blob>();
  readonly confirmed = output<Blob>();
  readonly cancelled = output<void>();

  protected readonly handles = SNIP_HANDLES;
  protected readonly handleCursor = handleCursor;

  protected readonly mode = signal<SupportEditorMode>('crop');
  protected readonly ready = signal(false);
  /** Tijdens het samenstellen van het resultaat: tweede klik op "gebruiken" negeren. */
  protected readonly busy = signal(false);
  /** Markeringen (kaders en pijlen) in bronpixels, in tekenvolgorde. */
  protected readonly annotations = signal<readonly SupportAnnotation[]>([]);
  /** Markeerselectie tijdens het slepen, in bronpixels. */
  protected readonly selection = signal<Rect | undefined>(undefined);
  /** Pijl tijdens het slepen, in bronpixels. */
  protected readonly arrowDraft = signal<Arrow | undefined>(undefined);
  /** Exportregio in bronpixels; na laden het volledige beeld. */
  protected readonly cropRect = signal<Rect | undefined>(undefined);
  /** Bronformaat als signal, zodat de kaderweergave ervan kan afleiden. */
  private readonly sourceSize = signal<Size | undefined>(undefined);
  /** Weergavebreedte van het beeld in CSS-pixels; volgt het paneel via een ResizeObserver. */
  private readonly displayWidth = signal(0);

  /** CSS-pixels per bronpixel. */
  private readonly displayScale = computed(() => {
    const size = this.sourceSize();
    const width = this.displayWidth();
    return size && size.width > 0 && width > 0 ? width / size.width : 1;
  });

  /** Crop-kader in CSS-pixels, voor de positionering van de kaderlaag. */
  protected readonly cropView = computed<Rect | undefined>(() => {
    const rect = this.cropRect();
    if (!rect) return undefined;
    const scale = this.displayScale();
    return { x: rect.x * scale, y: rect.y * scale, width: rect.width * scale, height: rect.height * scale };
  });

  protected readonly cropLabel = computed(() => {
    const rect = this.cropRect();
    return rect ? `${rect.width} × ${rect.height}` : '';
  });

  /** Afmetingenbox binnen het kader als er onder het kader geen plaats meer is. */
  protected readonly sizeLabelInside = computed(() => {
    const view = this.cropView();
    const size = this.sourceSize();
    if (!view || !size) return true;
    return view.y + view.height + SIZE_LABEL_SPACE_PX > size.height * this.displayScale();
  });

  private readonly imageCanvas = viewChild.required<ElementRef<HTMLCanvasElement>>('imageCanvas');
  private readonly overlayCanvas = viewChild.required<ElementRef<HTMLCanvasElement>>('overlayCanvas');
  private readonly destroyRef = inject(DestroyRef);
  private readonly notifier = inject(NOTIFIER);
  private readonly transloco = inject(TranslocoService);

  private readonly root = viewChild.required<ElementRef<HTMLElement>>('root');

  private base?: ImageBitmap;
  private dragStart?: Point;
  private cropDrag?: CropDrag;
  private loadToken = 0;

  constructor() {
    effect(() => {
      const blob = this.source();
      untracked(() => void this.load(blob));
    });
    afterNextRender(() => this.observeDisplayWidth());
    this.destroyRef.onDestroy(() => this.base?.close());
  }

  protected setMode(mode: SupportEditorMode): void {
    this.mode.set(mode);
    this.selection.set(undefined);
    this.arrowDraft.set(undefined);
    this.drawOverlay();
  }

  /** Laatste markering weghalen. */
  protected undo(): void {
    if (this.annotations().length === 0) return;
    this.annotations.update(list => list.slice(0, -1));
    this.drawOverlay();
  }

  protected onUndoKey(event: Event): void {
    event.preventDefault();
    this.undo();
  }

  protected reset(): void {
    void this.load(this.source());
  }

  protected cancel(): void {
    this.cancelled.emit();
  }

  /** Escape sluit alleen de editor; het paneel (dat ook op Escape luistert) blijft open. */
  protected onEscape(event: Event): void {
    event.stopPropagation();
    this.cancel();
  }

  /** Bron plus markering samenstellen en op het crop-kader uitsnijden. */
  protected async use(): Promise<void> {
    const base = this.base;
    const crop = this.cropRect();
    if (!base || !crop || this.busy()) return;
    this.busy.set(true);
    try {
      const composite = this.compose(base);
      if (!composite) return;
      const canvas = document.createElement('canvas');
      canvas.width = crop.width;
      canvas.height = crop.height;
      const context = canvas.getContext('2d');
      if (!context) return;
      context.drawImage(composite, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
      if (!blob) return;
      this.confirmed.emit(blob);
    } finally {
      this.busy.set(false);
    }
  }

  // Markeren: slepen op het overlay-canvas voegt een rood kader of een rode pijl toe.

  protected onPointerDown(event: PointerEvent): void {
    if (!this.base || this.mode() === 'crop' || event.button !== 0) return;
    event.preventDefault();
    this.dragStart = this.pointFrom(event);
    this.selection.set(undefined);
    this.arrowDraft.set(undefined);
    try {
      this.overlay().setPointerCapture(event.pointerId);
    } catch {
      // Synthetische events (specs) hebben geen actieve pointer; slepen werkt ook zonder capture.
    }
  }

  protected onPointerMove(event: PointerEvent): void {
    if (!this.dragStart) return;
    if (this.mode() === 'arrow') {
      this.arrowDraft.set(this.arrowFrom(this.dragStart, this.pointFrom(event)));
    } else {
      this.selection.set(this.rectFrom(this.dragStart, this.pointFrom(event)));
    }
    this.drawOverlay();
  }

  protected onPointerUp(event: PointerEvent): void {
    if (!this.dragStart) return;
    const start = this.dragStart;
    const end = this.pointFrom(event);
    this.dragStart = undefined;
    this.selection.set(undefined);
    this.arrowDraft.set(undefined);
    try {
      this.overlay().releasePointerCapture(event.pointerId);
    } catch {
      // Zie onPointerDown.
    }
    const annotation = this.annotationFrom(start, end);
    if (annotation) this.annotations.update(list => [...list, annotation]);
    this.drawOverlay();
  }

  /** Afgebroken sleep (bv. touch-scroll of verlies van capture): nooit iets toepassen. */
  protected onPointerCancel(): void {
    this.dragStart = undefined;
    this.selection.set(undefined);
    this.arrowDraft.set(undefined);
    this.drawOverlay();
  }

  private annotationFrom(start: Point, end: Point): SupportAnnotation | undefined {
    if (this.mode() === 'arrow') {
      const arrow = this.arrowFrom(start, end);
      return arrow ? { kind: 'arrow', arrow } : undefined;
    }
    const rect = this.rectFrom(start, end);
    return rect ? { kind: 'rect', rect } : undefined;
  }

  // Uitsnijden: handles slepen past het kader aan, slepen binnenin verplaatst het.

  protected onCropPointerDown(event: PointerEvent): void {
    const rect = this.cropRect();
    if (!this.base || this.mode() !== 'crop' || event.button !== 0 || !rect) return;
    if (!(event.currentTarget instanceof HTMLElement)) return;
    event.preventDefault();
    const element = event.currentTarget;
    const origin = { x: event.clientX, y: event.clientY };
    const scale = this.sourceScale();
    const handle = this.handleAt(event);
    this.cropDrag = handle
      ? { kind: 'resize', handle, origin, rect, scale, element }
      : { kind: 'move', origin, rect, scale, element };
    try {
      element.setPointerCapture(event.pointerId);
    } catch {
      // Zie onPointerDown.
    }
  }

  protected onCropPointerMove(event: PointerEvent): void {
    const drag = this.cropDrag;
    if (!drag) return;
    this.cropRect.set(this.applyCropDrag(drag, event));
  }

  protected onCropPointerUp(event: PointerEvent): void {
    const drag = this.cropDrag;
    if (!drag) return;
    this.cropRect.set(this.applyCropDrag(drag, event));
    this.endCropDrag(event.pointerId);
  }

  /** Bijstellen afgebroken: het kader van vóór de sleep komt terug. */
  protected onCropPointerCancel(event: PointerEvent): void {
    const drag = this.cropDrag;
    if (!drag) return;
    this.cropRect.set(drag.rect);
    this.endCropDrag(event.pointerId);
  }

  private applyCropDrag(drag: CropDrag, event: PointerEvent): Rect {
    if (!this.base) return drag.rect;
    // Afgerond in bronpixels: met een geheel kader en gehele verschuivingen blijft het kader geheel.
    const dx = Math.round((event.clientX - drag.origin.x) * drag.scale);
    const dy = Math.round((event.clientY - drag.origin.y) * drag.scale);
    const bounds = { width: this.base.width, height: this.base.height };
    return drag.kind === 'resize'
      ? resizeRect(drag.rect, drag.handle, dx, dy, bounds)
      : moveRect(drag.rect, dx, dy, bounds);
  }

  private endCropDrag(pointerId: number): void {
    const drag = this.cropDrag;
    this.cropDrag = undefined;
    if (!drag) return;
    try {
      if (drag.element.hasPointerCapture(pointerId)) drag.element.releasePointerCapture(pointerId);
    } catch {
      // Zie onPointerDown.
    }
  }

  private handleAt(event: Event): SnipHandle | undefined {
    if (!(event.target instanceof Element)) return undefined;
    const handle = event.target.closest('[data-handle]')?.getAttribute('data-handle');
    return SNIP_HANDLES.find(candidate => candidate === handle);
  }

  private async load(blob: Blob): Promise<void> {
    const token = ++this.loadToken;
    this.ready.set(false);
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(blob);
    } catch {
      if (token !== this.loadToken) return;
      this.notifier.error(this.transloco.translate<string>('support.form.attachments.invalid'));
      this.cancelled.emit();
      return;
    }
    if (token !== this.loadToken) {
      bitmap.close();
      return;
    }
    this.replaceBase(bitmap);
  }

  /** Nieuw basisbeeld: kader op het volledige beeld, markeringen weg. */
  private replaceBase(bitmap: ImageBitmap): void {
    this.base?.close();
    this.base = bitmap;
    this.cropDrag = undefined;
    this.annotations.set([]);
    this.selection.set(undefined);
    this.arrowDraft.set(undefined);
    this.sourceSize.set({ width: bitmap.width, height: bitmap.height });
    this.cropRect.set({ x: 0, y: 0, width: bitmap.width, height: bitmap.height });
    this.ready.set(true);
    this.drawImage();
    this.sizeOverlay();
    this.drawOverlay();
    // Meteen meten: de ResizeObserver meldt zich pas na de volgende layout.
    this.displayWidth.set(this.imageCanvas().nativeElement.clientWidth);
    this.root().nativeElement.focus({ preventScroll: true });
  }

  private observeDisplayWidth(): void {
    const canvas = this.imageCanvas().nativeElement;
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? canvas.clientWidth;
      this.displayWidth.set(width);
    });
    observer.observe(canvas);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  /** Alleen bij een nieuw basisbeeld: het formaat zetten wist het canvas en is duur tijdens het slepen. */
  private sizeOverlay(): void {
    if (!this.base) return;
    const canvas = this.overlay();
    canvas.width = this.base.width;
    canvas.height = this.base.height;
  }

  private drawImage(): void {
    if (!this.base) return;
    const canvas = this.imageCanvas().nativeElement;
    canvas.width = this.base.width;
    canvas.height = this.base.height;
    canvas.getContext('2d')?.drawImage(this.base, 0, 0);
  }

  private drawOverlay(): void {
    if (!this.base) return;
    const canvas = this.overlay();
    const context = canvas.getContext('2d');
    if (!context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    this.drawAnnotations(context, canvas.width);
    const draft = this.arrowDraft();
    if (draft) {
      context.save();
      context.globalAlpha = ARROW_DRAFT_ALPHA;
      this.strokeArrow(context, draft, canvas.width);
      context.restore();
    }
    const selection = this.selection();
    if (!selection) return;
    context.fillStyle = SELECTION_FILL;
    context.fillRect(selection.x, selection.y, selection.width, selection.height);
    context.strokeStyle = SELECTION_STROKE;
    context.lineWidth = Math.max(1, markStrokeWidth(canvas.width) / 2);
    context.setLineDash([6, 4]);
    context.strokeRect(selection.x, selection.y, selection.width, selection.height);
    context.setLineDash([]);
  }

  /** Bron met witte achtergrond en ingebakken markering, in bronformaat. */
  private compose(base: ImageBitmap): HTMLCanvasElement | undefined {
    const canvas = document.createElement('canvas');
    canvas.width = base.width;
    canvas.height = base.height;
    const context = canvas.getContext('2d');
    if (!context) return undefined;
    context.fillStyle = JPEG_BACKGROUND;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(base, 0, 0);
    this.drawAnnotations(context, base.width);
    return canvas;
  }

  private drawAnnotations(context: CanvasRenderingContext2D, sourceWidth: number): void {
    for (const annotation of this.annotations()) {
      if (annotation.kind === 'rect') {
        this.strokeMark(context, annotation.rect, sourceWidth);
      } else {
        this.strokeArrow(context, annotation.arrow, sourceWidth);
      }
    }
  }

  private strokeMark(context: CanvasRenderingContext2D, mark: Rect, sourceWidth: number): void {
    context.strokeStyle = MARK_COLOR;
    context.lineWidth = markStrokeWidth(sourceWidth);
    context.strokeRect(mark.x, mark.y, mark.width, mark.height);
  }

  /** Schacht tot aan de pijlpunt (ronde kap) plus een gevulde driehoek als punt. */
  private strokeArrow(context: CanvasRenderingContext2D, arrow: Arrow, sourceWidth: number): void {
    const lineWidth = markStrokeWidth(sourceWidth);
    const head = arrowHeadGeometry(arrow, lineWidth);
    context.strokeStyle = MARK_COLOR;
    context.fillStyle = MARK_COLOR;
    context.lineWidth = lineWidth;
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(arrow.from.x, arrow.from.y);
    context.lineTo(head.base.x, head.base.y);
    context.stroke();
    context.beginPath();
    context.moveTo(head.tip.x, head.tip.y);
    context.lineTo(head.left.x, head.left.y);
    context.lineTo(head.right.x, head.right.y);
    context.closePath();
    context.fill();
    context.lineCap = 'butt';
  }

  private overlay(): HTMLCanvasElement {
    return this.overlayCanvas().nativeElement;
  }

  /** Positie in weergavepixels, relatief aan het canvas. */
  private pointFrom(event: PointerEvent): Point {
    const bounds = this.overlay().getBoundingClientRect();
    return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  }

  /** Bronpixels per CSS-pixel, gemeten op het beeldcanvas. */
  private sourceScale(): number {
    const canvas = this.imageCanvas().nativeElement;
    return canvas.clientWidth > 0 ? canvas.width / canvas.clientWidth : 1;
  }

  private rectFrom(start: Point, end: Point): Rect | undefined {
    if (!this.base) return undefined;
    return toSourceRect(start, end, this.sourceScale(), { width: this.base.width, height: this.base.height });
  }

  private arrowFrom(start: Point, end: Point): Arrow | undefined {
    if (!this.base) return undefined;
    return toSourceArrow(start, end, this.sourceScale(), { width: this.base.width, height: this.base.height });
  }
}
